import { useState, useEffect, useCallback, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/domains/auth/useAuth";
import { v4 as uuidv4 } from "uuid";
import { useConfetti } from "@/components/ui/confetti-provider";
import { useQueryClient } from "@tanstack/react-query";

interface UseLikeProfileProps {
  profileId: string;
}

interface UseLikeProfileReturn {
  isLiked: boolean;
  likeCount: number;
  toggleLike: (e?: React.MouseEvent<HTMLElement>) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

// Function to get or create a persistent anonymous ID
const getAnonymousId = (): string => {
  try {
    let anonymousId = localStorage.getItem("anonymous_user_id");
    if (!anonymousId) {
      anonymousId = `anon-${uuidv4()}`;
      localStorage.setItem("anonymous_user_id", anonymousId);
    }
    return anonymousId;
  } catch (e) {
    // If localStorage is not available, generate a temporary ID
    return `anon-${uuidv4()}`;
  }
};

export const useLikeProfile = ({
  profileId,
}: UseLikeProfileProps): UseLikeProfileReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // Use refs to prevent infinite loops and track state changes
  const likeCountRef = useRef(0);
  const isLikedRef = useRef(false);
  const initializedRef = useRef(false);
  const lastCheckTimeRef = useRef(0);
  const toggleInProgressRef = useRef(false);

  const { toast } = useToast();
  const { user } = useAuth();
  const { showConfetti } = useConfetti();
  const queryClient = useQueryClient();

  // Debounced check function to prevent excessive API calls
  const checkLikeStatus = useCallback(async () => {
    if (!profileId || toggleInProgressRef.current) return;

    const now = Date.now();
    // Prevent checking too frequently (minimum 2 seconds between checks)
    if (now - lastCheckTimeRef.current < 2000 && initializedRef.current) {
      return;
    }

    lastCheckTimeRef.current = now;

    try {
      // First check localStorage for quick UI feedback
      const likedProfiles = JSON.parse(
        localStorage.getItem("likedProfiles") || "[]"
      );
      const isLikedInStorage = likedProfiles.includes(profileId);

      // Only update state if it's different and we're not in a toggle operation
      if (
        !toggleInProgressRef.current &&
        isLikedInStorage !== isLikedRef.current
      ) {
        setIsLiked(isLikedInStorage);
        isLikedRef.current = isLikedInStorage;
      }

      // Then verify against the database
      const userId = user?.id || null;
      const anonymousId = userId ? null : getAnonymousId();

      const { data, error } = await supabase.functions.invoke(
        "handle-profile-like",
        {
          body: {
            action: "check",
            profileId,
            userId,
            anonymousId,
          },
        }
      );

      if (error) {
        console.error("[useLikeProfile] Error checking like status:", error);
        return;
      }

      if (data && !toggleInProgressRef.current) {
        const newIsLiked = !!data.liked;
        const newLikeCount = data.like_count || 0;

        // Only update if values have actually changed
        if (
          newIsLiked !== isLikedRef.current ||
          newLikeCount !== likeCountRef.current
        ) {
          setIsLiked(newIsLiked);
          setLikeCount(newLikeCount);
          isLikedRef.current = newIsLiked;
          likeCountRef.current = newLikeCount;

          // Sync localStorage with database state
          if (newIsLiked !== isLikedInStorage) {
            const updatedLikedProfiles = newIsLiked
              ? [...likedProfiles, profileId]
              : likedProfiles.filter((id: string) => id !== profileId);
            localStorage.setItem(
              "likedProfiles",
              JSON.stringify(updatedLikedProfiles)
            );
          }
        }
      }

      initializedRef.current = true;
    } catch (err) {
      console.error("[useLikeProfile] Error in checkLikeStatus:", err);
    }
  }, [profileId, user?.id]);

  // Initialize on mount or when profileId changes
  useEffect(() => {
    if (profileId && !initializedRef.current) {
      checkLikeStatus();
    }
  }, [profileId, checkLikeStatus]);

  // Optimized toggle function with better error handling
  const toggleLike = useCallback(
    async (e?: React.MouseEvent<HTMLElement>) => {
      if (isLoading || !profileId || toggleInProgressRef.current) {
        return;
      }

      toggleInProgressRef.current = true;
      setIsLoading(true);
      setError(null);

      // Store current state for potential rollback
      const previousLiked = isLikedRef.current;
      const previousCount = likeCountRef.current;

      try {
        // Optimistic UI update
        const newLikedState = !previousLiked;
        const newLikeCount = newLikedState
          ? previousCount + 1
          : Math.max(0, previousCount - 1);

        // Update state immediately
        setIsLiked(newLikedState);
        setLikeCount(newLikeCount);
        isLikedRef.current = newLikedState;
        likeCountRef.current = newLikeCount;

        // Update localStorage for immediate feedback
        try {
          const likedProfiles = JSON.parse(
            localStorage.getItem("likedProfiles") || "[]"
          );
          const updatedLikedProfiles = newLikedState
            ? [...likedProfiles, profileId]
            : likedProfiles.filter((id: string) => id !== profileId);
          localStorage.setItem(
            "likedProfiles",
            JSON.stringify(updatedLikedProfiles)
          );
        } catch (err) {
          console.warn("[useLikeProfile] Error updating localStorage:", err);
        }

        // Show confetti effect if liking
        if (newLikedState && e?.currentTarget) {
          const targetElement = e.currentTarget as HTMLElement;
          const rect = targetElement.getBoundingClientRect();
          showConfetti({
            x: rect.left + rect.width / 2,
            y: rect.top + rect.height / 2,
            width: rect.width * 3,
            height: 10,
          });
        }

        // Call the edge function
        const { data: result, error } = await supabase.functions.invoke(
          "handle-profile-like",
          {
            body: {
              action: "toggle",
              profileId,
              userId: user?.id || null,
              anonymousId: user?.id ? null : getAnonymousId(),
            },
          }
        );

        if (error) throw error;

        // Update with accurate database values
        if (result) {
          const finalLiked = result.liked;
          const finalCount = result.like_count;

          setIsLiked(finalLiked);
          setLikeCount(finalCount);
          isLikedRef.current = finalLiked;
          likeCountRef.current = finalCount;

          // Show success toast only for like actions
          if (finalLiked) {
            toast({
              title: "Profile liked",
              description: "You have successfully liked this profile.",
              duration: 3000,
            });
          }
        }
      } catch (err: unknown) {
        console.error("[useLikeProfile] Error toggling like:", err);

        // Revert optimistic updates
        setIsLiked(previousLiked);
        setLikeCount(previousCount);
        isLikedRef.current = previousLiked;
        likeCountRef.current = previousCount;

        // Revert localStorage changes
        try {
          const likedProfiles = JSON.parse(
            localStorage.getItem("likedProfiles") || "[]"
          );
          const updatedLikedProfiles = previousLiked
            ? [...likedProfiles, profileId]
            : likedProfiles.filter((id: string) => id !== profileId);
          localStorage.setItem(
            "likedProfiles",
            JSON.stringify(updatedLikedProfiles)
          );
        } catch (storageErr) {
          console.warn(
            "[useLikeProfile] Error reverting localStorage:",
            storageErr
          );
        }

        const errorMessage =
          err instanceof Error ? err.message : "Failed to update like status";
        setError(errorMessage);
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        toggleInProgressRef.current = false;
      }
    },
    [profileId, user?.id, showConfetti, toast]
  );

  return {
    isLiked,
    likeCount,
    toggleLike,
    isLoading,
    error,
  };
};
